import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CreatePresupuestoRequest } from '@visep/shared-types/presupuestos';
import { api } from '@/lib/api';
import { useFormSubmit } from '@/lib/use-form-submit';
import { glassInput, glassLabel } from '@/components/landing/glass';
import { WIZARD_PATHS, type WizardPath } from '@/data/wizard-paths';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

interface ContactData {
  nombre: string;
  telefono: string;
  email: string;
  mensaje: string;
}

const EMPTY_CONTACT: ContactData = { nombre: '', telefono: '', email: '', mensaje: '' };

export function QuotationWizard() {
  const [path, setPath] = useState<WizardPath | null>(null);
  const [step, setStep] = useState(0); // 0 = seleção de caminho; 1..N perguntas; N+1 form
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [contact, setContact] = useState<ContactData>(EMPTY_CONTACT);
  const [terminos, setTerminos] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const {
    status,
    errorMessage,
    submit,
    reset: resetSubmit,
  } = useFormSubmit((input: CreatePresupuestoRequest) => api.presupuestos.create(input));

  const totalSteps = path ? path.questions.length + 1 : 0;
  // B3 fix: o contador nunca ultrapassa o total (origem mostrava "PASO 7/6").
  const displayStep = Math.min(step, totalSteps);
  const onFormStep = path !== null && step === path.questions.length + 1;

  function resetAll() {
    setPath(null);
    setStep(0);
    setAnswers({});
    setContact(EMPTY_CONTACT);
    setTerminos(false);
    setHoneypot('');
    setLocalError(null);
    resetSubmit();
  }

  function choosePath(p: WizardPath) {
    setPath(p);
    setStep(1);
  }

  function answerQuestion(questionId: string, option: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: option }));
    setStep((s) => s + 1);
  }

  function goBack() {
    if (step <= 1) {
      resetAll();
      return;
    }
    setStep((s) => s - 1);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!path) return;
    setLocalError(null);
    if (!contact.nombre.trim() || !contact.email.trim()) {
      setLocalError('Ingresá tu nombre y correo electrónico.');
      return;
    }
    if (!terminos) {
      setLocalError('Necesitamos tu consentimiento para contactarte.');
      return;
    }
    const input: CreatePresupuestoRequest = {
      nombre: contact.nombre.trim(),
      email: contact.email.trim(),
      tipoPropiedad: path.id,
      respuestas: answers,
      terminosAceptados: true,
      ...(contact.telefono.trim() ? { telefono: contact.telefono.trim() } : {}),
      ...(contact.mensaje.trim() ? { mensaje: contact.mensaje.trim() } : {}),
    };
    const ok = await submit(input, honeypot);
    if (ok) {
      toast.success('¡Solicitud enviada! Te contactaremos en menos de 24 horas.');
    }
  }

  if (status === 'success') {
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-success" aria-hidden />
        <h3 className="mt-4 text-2xl font-bold text-white">¡Solicitud Exitosa!</h3>
        <p className="mt-2 text-slate-400">
          Gracias por confiar en VISEP. Nuestro equipo te contactará en menos de 24 horas.
        </p>
        <Button className="mt-6" onClick={resetAll}>
          Volver al inicio
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-8">
      {step > 0 && (
        <div className="mb-6 flex items-center justify-between">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Atrás
          </button>
          <span className="text-sm font-medium text-slate-400">
            Paso {displayStep} de {totalSteps}
          </span>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${path?.id ?? 'root'}-${step}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2 }}
        >
          {step === 0 && (
            <div>
              <h3 className="text-xl font-bold">¿Qué querés proteger?</h3>
              <p className="mt-1 text-sm text-slate-400">
                Respondé unas breves preguntas y obtené un presupuesto estimado.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {WIZARD_PATHS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => choosePath(p)}
                      className="flex flex-col items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 text-center transition-colors hover:border-primary/60 hover:bg-white/[0.05]"
                    >
                      <Icon className="h-8 w-8 text-primary" strokeWidth={1.5} aria-hidden />
                      <span className="font-semibold text-white">{p.label}</span>
                      <span className="text-xs text-slate-400">{p.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {path && step >= 1 && !onFormStep && (
            <fieldset>
              <legend className="text-xl font-bold">{path.questions[step - 1]!.question}</legend>
              <div className="mt-6 grid gap-3">
                {path.questions[step - 1]!.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => answerQuestion(path.questions[step - 1]!.id, option)}
                    className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-left font-medium text-slate-300 transition-colors hover:border-primary/60 hover:bg-white/[0.05] hover:text-white"
                  >
                    {option}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {onFormStep && (
            <form onSubmit={handleSubmit} noValidate>
              <h3 className="text-xl font-bold">¡Último paso!</h3>
              <p className="mt-1 text-sm text-slate-400">
                Dejanos tus datos y preparamos tu presupuesto.
              </p>
              <div className="mt-6 grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="wizard-nombre" className={glassLabel}>Nombre completo</Label>
                  <Input
                    id="wizard-nombre"
                    className={glassInput}
                    name="nombre"
                    autoComplete="name"
                    value={contact.nombre}
                    onChange={(e) => setContact({ ...contact, nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="wizard-telefono" className={glassLabel}>Teléfono / WhatsApp</Label>
                  <Input
                    id="wizard-telefono"
                    className={glassInput}
                    name="telefono"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+595 9XX XXX XXX"
                    value={contact.telefono}
                    onChange={(e) => setContact({ ...contact, telefono: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="wizard-email" className={glassLabel}>Correo electrónico</Label>
                  <Input
                    id="wizard-email"
                    className={glassInput}
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="wizard-mensaje" className={glassLabel}>Mensaje (opcional)</Label>
                  <Textarea
                    id="wizard-mensaje"
                    className={glassInput}
                    name="mensaje"
                    value={contact.mensaje}
                    onChange={(e) => setContact({ ...contact, mensaje: e.target.value })}
                  />
                </div>
                {/* Honeypot anti-spam — oculto a usuários e leitores de tela. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
                <label className="flex items-start gap-3 text-sm text-slate-400">
                  <Checkbox
                    name="terminos"
                    checked={terminos}
                    onCheckedChange={(v) => setTerminos(v === true)}
                    aria-label="Consentimiento de contacto"
                  />
                  <span>
                    Acepto que VISEP me contacte para enviarme mi presupuesto y ofertas
                    relacionadas.
                  </span>
                </label>
                {(localError || errorMessage) && (
                  <p className="text-sm text-error" role="alert">
                    {localError ?? errorMessage}
                  </p>
                )}
                <Button type="submit" size="lg" disabled={status === 'loading'}>
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> Enviando…
                    </>
                  ) : (
                    'Obtener Mi Presupuesto'
                  )}
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
